import { useInfiniteQuery } from '@tanstack/react-query';
import { Client } from 'xrpl';
import { useXRPLContext } from '../contexts';

interface Transaction {
  account: string; // Sender address
  hash: string;
  date: string;
  type: string;
  amount: string;
  fee: string;
  result: string;
  delivered_amount: string | { currency: string; issuer: string; value: string };
  limit_amount?: { currency: string; issuer: string; value: string }; // For TrustSet transactions
  destination?: string; // Receiver address for Payment transactions
  taker_gets?: string | { currency: string; issuer: string; value: string }; // For OfferCreate transactions
  taker_pays?: string | { currency: string; issuer: string; value: string }; // For OfferCreate transactions
  offer_sequence?: number; // For OfferCancel transactions
  meta?: any; // Full metadata including AffectedNodes for parsing execution details
}

interface TransactionHistoryParams {
  account: string;
  network: string;
  pageSize?: number;
}

interface TransactionHistoryResponse {
  transactions: Transaction[];
  nextCursor?: any; // XRPL marker is an object, not a string
  hasNextPage: boolean;
}

// Fetch function for a single page of transactions
async function fetchTransactionPage(
  account: string,
  pageSize: number,
  client: Client,
  cursor?: any
): Promise<TransactionHistoryResponse> {
  if (!account) {
    throw new Error('Invalid account or network');
  }

  try {
    await client.connect();

    const response = await client.request({
      command: 'account_tx',
      account: account,
      limit: pageSize,
      marker: cursor,
    });

    const txData = response.result as { transactions: any[]; marker?: string };

    // Filter out spam transactions (delivered_amount === 1)
    const filteredTransactions = txData.transactions
      .filter((tx) => tx && tx.tx_json)
      .filter((tx) => {
        const transaction = tx.tx_json;
        if (
          transaction.TransactionType === 'Payment' &&
          tx.meta?.delivered_amount
        ) {
          const deliveredAmount = tx.meta.delivered_amount;
          if (deliveredAmount === 1 || deliveredAmount === '1') {
            return false;
          }
        }
        return true;
      })
      .map((tx) => {
        const transaction = tx.tx_json;
        return {
          account: transaction.Account || '',
          hash: tx.hash || '',
          date: tx.close_time_iso || new Date().toISOString(),
          type: transaction.TransactionType || 'Unknown',
          amount: '0',
          fee: transaction.Fee || '0',
          result: tx.meta?.TransactionResult || 'Unknown',
          delivered_amount: tx.meta?.delivered_amount || '0',
          limit_amount: transaction.LimitAmount, // For TrustSet transactions
          destination: transaction.Destination, // For Payment transactions
          taker_gets: transaction.TakerGets, // For OfferCreate transactions
          taker_pays: transaction.TakerPays, // For OfferCreate transactions
          offer_sequence: transaction.OfferSequence, // For OfferCancel transactions
          meta: tx.meta, // Include full metadata for parsing execution details
        };
      });

    await client.disconnect();

    return {
      transactions: filteredTransactions,
      nextCursor: txData.marker || undefined,
      hasNextPage: !!txData.marker,
    };
  } catch (error) {
    await client.disconnect();
    throw error;
  }
}

export function useTransactionHistory({
  account,
  network,
  pageSize = 10,
}: TransactionHistoryParams) {
  const { client } = useXRPLContext();

  return useInfiniteQuery({
    queryKey: ['transactionHistory', account, network, pageSize, client.url],
    queryFn: ({ pageParam }) =>
      fetchTransactionPage(account, pageSize, client, pageParam),
    initialPageParam: undefined as any,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!account,
    staleTime: 0, // Always refetch when network changes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}
